import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "@phosphor-icons/react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Marquee from "../components/Marquee";
import FAQSection from "../components/FAQSection";
import { api } from "../lib/api";

const ROTATING = ["SaaS scale-ups.", "product teams.", "AI start-ups.", "enterprise GCCs."];

const SERVICE_MODULES = [
  "HUMINT · HUMAN INTELLIGENCE FOR THE FUTURE OF WORK",
  "HUMINT · HUMAN INTELLIGENCE FOR THE FUTURE OF WORK",
  "HUMINT · HUMAN INTELLIGENCE FOR THE FUTURE OF WORK",
  "HUMINT · HUMAN INTELLIGENCE FOR THE FUTURE OF WORK",
  "HUMINT · HUMAN INTELLIGENCE FOR THE FUTURE OF WORK",
  "HUMINT · HUMAN INTELLIGENCE FOR THE FUTURE OF WORK",
];

const IMPACTS = [
  { k: "3.1×", label: "Lift in psychological-safety pulse" },
  { k: "87%", label: "Report a real sense of belonging" },
  { k: "+34 pts", label: "eNPS lift within six months" },
];

function Hero() {
  return (
    <section data-testid="hero" className="relative overflow-hidden min-h-[92vh] pt-28 md:pt-32 pb-10 grid-bg">
      <div className="absolute inset-0 beam pointer-events-none" />
      <div className="relative mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 md:gap-10 items-start">
          {/* Left copy */}
          <div className="col-span-12 lg:col-span-7">
            <div className="inline-flex items-center gap-3 rise d-1">
              <span className="w-2 h-2 bg-cy block" />
              <span className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase">
                HUMINT · HUMAN INTELLIGENCE FOR THE FUTURE OF WORK
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="mt-8 font-display font-black tracking-[-0.04em] leading-[0.92] text-white text-6xl sm:text-7xl md:text-[108px]"
            >
              <span className="block rise d-2">DIAGNOSE.</span>
              <span className="block rise d-3">DISCOVER.</span>
              <span className="block text-cy rise d-4">DELIVER.</span>
            </h1>

            <p className="mt-10 max-w-xl text-zinc-300 rise d-5">
              <span className="text-zinc-400">The new HRBP playbook for</span>{" "}
              <span className="relative inline-flex h-[1.5em] overflow-hidden align-bottom">
                <span className="flex flex-col rot-track text-cy font-semibold">
                  {[...ROTATING, ROTATING[0]].map((w, i) => (
                    <span key={i} className="h-[1.5em] leading-[1.5em]">
                      {w}
                    </span>
                  ))}
                </span>
              </span>
              <br />
              <span className="text-zinc-400">
                We help growing organisations in Bangalore turn workforce challenges into
                data-driven outcomes.
              </span>
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3 rise d-6">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-3 bg-cy text-ink px-7 py-4 text-sm font-semibold hover:bg-white transition-colors"
              >
                Start My HR Diagnosis
                <ArrowUpRight
                  size={16}
                  weight="bold"
                  className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-3 border border-white/60 text-white px-7 py-4 text-sm font-semibold hover:bg-white hover:text-ink transition-colors"
              >
                Explore the Playbook
                <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>

          {/* Right: impact stats */}
          <div className="col-span-12 lg:col-span-5 lg:pl-8 rise d-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-cy mb-4">
              / Average Impact
            </div>
            <div className="border-t border-ink-500">
              {IMPACTS.map((i, idx) => (
                <div
                  key={idx}
                  className="flex items-end justify-between py-5 md:py-6 border-b border-ink-500"
                >
                  <span className="font-display text-5xl md:text-6xl font-extrabold text-white tracking-tight">
                    {i.k}
                  </span>
                  <span className="text-sm text-zinc-400">{i.label}</span>
                </div>
              ))}
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500">
                Bangalore client base · 2024–25
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyStrip() {
  const points = [
    "Hyper-competitive AI talent markets",
    "Hybrid work fatigue and disengagement",
    "Skill obsolescence outpacing L&D",
    "Culture dilution after scale-ups or funding rounds",
  ];
  return (
    <section id="why" data-testid="why-strip" className="bg-[#070709] border-y border-ink-600">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-5">
            <div className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase mb-4">
              01 · The Why
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white leading-tight tracking-tight">
              Modern work is evolving
              <span className="block text-zinc-500 italic font-medium">
                faster than your HR playbook.
              </span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7">
            <ul className="border-t border-ink-500">
              {points.map((p, i) => (
                <li
                  key={i}
                  data-testid={`why-point-${i}`}
                  className="group flex items-center justify-between gap-6 py-5 border-b border-ink-500 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex items-center gap-5">
                    <span className="font-mono text-[11px] text-cy">0{i + 1}</span>
                    <span className="text-base md:text-lg text-white">{p}</span>
                  </div>
                  <ArrowRight
                    size={18}
                    weight="bold"
                    className="text-zinc-600 group-hover:text-cy group-hover:translate-x-1 transition-all"
                  />
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm md:text-base text-zinc-400 max-w-xl">
              You don&apos;t need more policies — you need a thinking HR partner who bridges{" "}
              <span className="text-white">people data</span>,{" "}
              <span className="text-white">business goals</span>, and{" "}
              <span className="text-white">culture outcomes</span>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesTeaser() {
  const six = [
    ["01", "Strategic HR Business Partnering"],
    ["02", "Talent Acquisition & Employer Brand"],
    ["03", "Retention, Engagement & Culture"],
    ["04", "Performance & Capability"],
    ["05", "Compensation, Benefits & Rewards"],
    ["06", "HR Operations, Data & Compliance"],
  ];
  return (
    <section id="services-teaser" data-testid="services-teaser" className="bg-[#050505]">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-10 mb-10">
          <div className="col-span-12 md:col-span-6">
            <div className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase mb-4">
              02 · What We Deliver
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white leading-tight tracking-tight">
              Modular HR advisory
              <span className="block text-zinc-500 italic font-medium">for modern orgs.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 flex items-end">
            <p className="text-sm md:text-base text-zinc-400">
              Every company faces similar people challenges — but no two cultures, data sets, or
              growth stages are alike. Our advisory model adapts to your business maturity and
              tech-talent dynamics.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-ink-500">
          {six.map(([num, title]) => (
            <Link
              key={num}
              to="/services"
              data-testid={`service-teaser-${num}`}
              className="group relative border-r border-b border-ink-500 p-6 md:p-8 bg-[#070709] hover:bg-[#0B0B10] transition-colors"
            >
              <div className="flex items-start justify-between mb-8">
                <span className="font-mono text-[11px] text-cy">/ {num}</span>
                <ArrowUpRight
                  size={18}
                  weight="bold"
                  className="text-zinc-600 group-hover:text-cy transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </div>
              <h3 className="font-display text-lg md:text-xl font-bold text-white tracking-tight">
                {title}
              </h3>
            </Link>
          ))}
        </div>

        <div className="mt-10 flex justify-end">
          <Link
            to="/services"
            data-testid="view-all-services"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white hover:text-cy"
          >
            View all services <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function InsightsTeaser() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api
      .get("/insights")
      .then((r) => setItems((r.data || []).slice(0, 3)))
      .catch(() => setItems([]));
  }, []);

  return (
    <section data-testid="insights-teaser" className="bg-[#070709] border-t border-ink-600">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-20 md:py-28">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase mb-4">
              03 · Field Notes
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white leading-tight tracking-tight">
              Signals from the floor.
            </h2>
          </div>
          <Link
            to="/insights"
            data-testid="view-all-insights"
            className="hidden sm:inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white hover:text-cy"
          >
            View all <ArrowRight size={14} weight="bold" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-0 border-t border-l border-ink-500">
          {items.map((p) => (
            <Link
              key={p.id}
              to={`/insights#${p.slug}`}
              data-testid={`insight-card-${p.slug}`}
              className="group p-6 md:p-8 border-r border-b border-ink-500 bg-[#050505] hover:bg-[#0B0B10] transition-colors"
            >
              <div className="flex items-center gap-4 mb-4">
                <span className="font-mono text-[10px] text-cy uppercase tracking-[0.22em]">
                  {p.category}
                </span>
                <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.22em]">
                  {p.read_time}
                </span>
              </div>
              <h3 className="font-display text-xl font-bold text-white tracking-tight leading-snug">
                {p.title}
              </h3>
              <p className="mt-3 text-sm text-zinc-400 line-clamp-3">{p.excerpt}</p>
            </Link>
          ))}
          {items.length === 0 && (
            <div className="p-10 text-zinc-500 text-sm border-r border-b border-ink-500">
              Loading field notes…
            </div>
          )}
        </div>
      </div>
    </section>
  );
}


function CTAStrip() {
  return (
    <section data-testid="home-cta-strip" className="relative bg-[#050505] border-t border-ink-600 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
      <div className="relative mx-auto max-w-[1440px] px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-4xl">
          <div className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase border-t border-cy pt-3 inline-block">
            Ready when you are
          </div>
          <h2 className="mt-10 font-display text-4xl md:text-6xl lg:text-[88px] font-black tracking-[-0.03em] leading-[0.95] text-white">
            Let&apos;s map your first{" "}
            <span className="text-cy">HR diagnostic</span> together.
          </h2>
          <div className="mt-12">
            <Link
              to="/contact"
              data-testid="strip-cta"
              className="group inline-flex items-center gap-3 bg-cy text-ink px-10 py-5 text-sm font-semibold hover:bg-white transition-colors"
            >
              Start My HR Diagnosis
              <ArrowUpRight
                size={18}
                weight="bold"
                className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-[#050505] text-white">
      <Header />
      <main>
        <Marquee items={SERVICE_MODULES} variant="brand" testid="hero-marquee" />
        <Hero />
        <WhyStrip />
        <ServicesTeaser />
        <InsightsTeaser />
        <FAQSection />
        <CTAStrip />
      </main>
      <Footer />
    </div>
  );
}
